package com.schoolconnect.app.repository.schoolusers

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.PlatformUsersResponse
import com.schoolconnect.app.datasource.network.response.SchoolUsersAnalyticsResponse
import com.schoolconnect.app.repository.common.CachedValue
import com.schoolconnect.app.repository.common.loadRefreshableCachedValue
import com.schoolconnect.app.repository.common.returnCachedAndRefresh
import com.schoolconnect.app.repository.common.tenantCacheKey
import com.schoolconnect.app.util.scLog
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob

object SchoolUsersRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] SchoolUsersRepository"
    private val SCHOOL_USER_ANALYTICS_ROLE_IDS = listOf(4, 5)

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private data class SchoolUsersPageCacheKey(
        val query: String,
        val schoolId: String?,
        val roleIds: List<Int>,
        val isActive: Boolean?,
        val createdFrom: String,
        val createdTo: String,
        val pageIndex: Int,
        val pageSize: Int,
        val sort: String
    )

    private data class CachedSchoolUsersPage(
        val key: SchoolUsersPageCacheKey,
        val response: PlatformUsersResponse
    )

    private var cachedSchoolUsersPage: CachedSchoolUsersPage? = null
    private val cachedSchoolUsersAnalytics = mutableMapOf<String, CachedValue<SchoolUsersAnalyticsResponse>>()

    suspend fun loadSchoolUsersPage(
        query: String = "",
        schoolId: String? = null,
        roleIds: List<Int> = emptyList(),
        isActive: Boolean? = null,
        createdFrom: String = "",
        createdTo: String = "",
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sort: ServerSort = ServerSort("email", com.schoolconnect.app.feature.common.ServerSortOrder.ASC),
        forceRemote: Boolean = false
    ): Result<PlatformUsersResponse> {
        val normalizedQuery = query.trim()
        val normalizedSchoolId = schoolId?.trim()?.takeIf { it.isNotBlank() }
        val normalizedRoleIds = roleIds.distinct().sorted()
        val normalizedCreatedFrom = createdFrom.trim()
        val normalizedCreatedTo = createdTo.trim()
        val safePageIndex = pageIndex.coerceAtLeast(0)
        val safePageSize = pageSize.coerceAtLeast(1)
        val cacheKey = SchoolUsersPageCacheKey(
            query = normalizedQuery,
            schoolId = normalizedSchoolId,
            roleIds = normalizedRoleIds,
            isActive = isActive,
            createdFrom = normalizedCreatedFrom,
            createdTo = normalizedCreatedTo,
            pageIndex = safePageIndex,
            pageSize = safePageSize,
            sort = sort.cacheKey
        )

        cachedSchoolUsersPage?.takeIf { !forceRemote && it.key == cacheKey }?.let { cache ->
            scLog(
                "$TAG loadSchoolUsersPage cacheHit q=${normalizedQuery.ifBlank { "<blank>" }} school=${normalizedSchoolId ?: "<any>"} roles=${normalizedRoleIds.joinToString(",").ifBlank { "<any>" }} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize count=${cache.response.users.orEmpty().size}"
            )
            return returnCachedAndRefresh(scope, TAG, "loadSchoolUsersPage", "school=${normalizedSchoolId ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize", cache.response) {
                loadSchoolUsersPage(query, normalizedSchoolId, normalizedRoleIds, isActive, normalizedCreatedFrom, normalizedCreatedTo, safePageIndex, safePageSize, sort, true)
            }
        }

        val offset = safePageIndex * safePageSize
        scLog(
            "$TAG loadSchoolUsersPage start q=${normalizedQuery.ifBlank { "<blank>" }} school=${normalizedSchoolId ?: "<any>"} roles=${normalizedRoleIds.joinToString(",").ifBlank { "<any>" }} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize offset=$offset forceRemote=$forceRemote"
        )
        return runCatching {
            val response = KtorApiClient.getUsers(
                q = normalizedQuery.ifBlank { null },
                schoolId = normalizedSchoolId,
                roleIds = normalizedRoleIds,
                isActive = isActive,
                createdFrom = normalizedCreatedFrom.ifBlank { null },
                createdTo = normalizedCreatedTo.ifBlank { null },
                limit = safePageSize,
                offset = offset,
                sortBy = sort.normalizedField,
                order = sort.order.apiValue
            ).getOrElse { throw it }

            // Keep one row per school assignment (same person at two schools = two rows).
            val deduped = response.copy(
                users = response.users.orEmpty().distinctBy {
                    it.assignment?.assignmentId.orEmpty().ifBlank { it.id.orEmpty() }
                }
            )
            cachedSchoolUsersPage = CachedSchoolUsersPage(cacheKey, deduped)
            scLog(
                "$TAG loadSchoolUsersPage complete q=${normalizedQuery.ifBlank { "<blank>" }} school=${normalizedSchoolId ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize total=${deduped.count ?: 0} count=${deduped.users.orEmpty().size}"
            )
            deduped
        }.onFailure { throwable ->
            scLog(
                "$TAG loadSchoolUsersPage failure q=${normalizedQuery.ifBlank { "<blank>" }} school=${normalizedSchoolId ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize",
                throwable
            )
        }
    }

    suspend fun loadSchoolUsersAnalytics(
        schoolId: String? = null,
        forceRemote: Boolean = false
    ): Result<SchoolUsersAnalyticsResponse> {
        val normalizedSchoolId = schoolId?.trim()?.takeIf { it.isNotBlank() }
        val cacheKey = tenantCacheKey("school-users-analytics:${normalizedSchoolId ?: "platform"}")

        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadSchoolUsersAnalytics",
            context = "school=${normalizedSchoolId ?: "<any>"} roles=${SCHOOL_USER_ANALYTICS_ROLE_IDS.joinToString(",")} forceRemote=$forceRemote",
            cachedValue = if (forceRemote) null else cachedSchoolUsersAnalytics[cacheKey],
            fetchValue = {
                KtorApiClient.getUsersAnalytics(
                    schoolId = normalizedSchoolId,
                    roleIds = SCHOOL_USER_ANALYTICS_ROLE_IDS
                )
            },
            updateCache = { response, fetchedAt ->
                cachedSchoolUsersAnalytics[cacheKey] = CachedValue(response, fetchedAt)
            },
            successDetails = { response ->
                "scope=${response.scope.ifBlank { "<blank>" }} total=${response.metrics.totalUsers} active=${response.metrics.activeUsers} inactive=${response.metrics.inactiveUsers}"
            }
        )
    }

    fun invalidateCaches() {
        cachedSchoolUsersPage = null
        cachedSchoolUsersAnalytics.clear()
    }
}
